import React from 'react'
import { EventSourcePolyfill, MessageEvent } from 'event-source-polyfill'
import { api } from '../function/axios'
import { refreshToken } from '../store/auth/service'

/*
 * Support alerts for the signed-in admin (espy-backend notifications): a new
 * ticket, or a customer's comment — for the ticket's assignee, or every admin
 * while it has none. One live feed for the whole panel (GET
 * /notifications/stream), whoever uses it: the menu's counts, the pop-up in
 * the corner, the bell, the support page. Counts are the unseen ones; opening
 * a ticket sees its alerts (POST /notifications/support-read).
 */

export type SupportAlert = { id: number; title: string; text: string; kind: 'ticket' | 'comment'; ticketId: number; createdTime: string }
export type SupportCounts = { tickets: number; comments: number }

const RECONNECT_MS = 30 * 1000

let counts: SupportCounts = { tickets: 0, comments: 0 }
const countListeners = new Set<(c: SupportCounts) => void>()
const alertListeners = new Set<(a: SupportAlert) => void>()
let source: EventSourcePolyfill | null = null
let retry: number | null = null
let users = 0

function setCounts(next: SupportCounts) {
  counts = next
  countListeners.forEach((f) => f(counts))
}

async function loadCounts() {
  try {
    const { data } = await api({ method: 'GET', url: '/notifications/support-counts' })
    setCounts({ tickets: Number(data?.tickets) || 0, comments: Number(data?.comments) || 0 })
  } catch { /* stays as it was */ }
}

function connect() {
  if (source || !users) return
  refreshToken().then((token: string) => {
    if (source || !users) return
    source = new EventSourcePolyfill(`${process.env.REACT_APP_API_BASE_URL}/notifications/stream`, {
      headers: { Authorization: `Bearer ${token}` },
      heartbeatTimeout: 24 * 60 * 60 * 1000,
    })
    source.onmessage = (event: MessageEvent) => {
      try {
        const alert: SupportAlert = JSON.parse(event.data)
        setCounts({
          tickets: counts.tickets + (alert.kind === 'ticket' ? 1 : 0),
          comments: counts.comments + (alert.kind === 'comment' ? 1 : 0),
        })
        alertListeners.forEach((f) => f(alert))
      } catch { /* not an alert */ }
    }
    source.onerror = () => {
      source?.close()
      source = null
      if (retry === null && users) {
        retry = window.setTimeout(() => { retry = null; void loadCounts(); connect() }, RECONNECT_MS)
      }
    }
  }).catch(() => { /* signed out */ })
}

function disconnect() {
  source?.close()
  source = null
  if (retry !== null) { window.clearTimeout(retry); retry = null }
}

/** all the admin's notifications were deleted (the bell's "Clear all") — no unseen alerts left */
export function clearSupportCounts() {
  setCounts({ tickets: 0, comments: 0 })
}

/** a ticket was opened — its alerts are seen; none given — all of them (Support was opened from the menu) */
export async function markTicketSeen(ticketId?: number) {
  if (!ticketId) setCounts({ tickets: 0, comments: 0 })
  try {
    const { data } = await api({ method: 'POST', url: '/notifications/support-read', body: ticketId ? { ticketId } : {} })
    if (data) setCounts({ tickets: Number(data.tickets) || 0, comments: Number(data.comments) || 0 })
  } catch { /* the api layer reports it */ }
}

/** the unseen counts, kept live; `onAlert` hears each new alert */
export function useSupportAlerts(onAlert?: (a: SupportAlert) => void): SupportCounts {
  const [value, setValue] = React.useState<SupportCounts>(counts)
  const alertRef = React.useRef(onAlert)
  alertRef.current = onAlert

  React.useEffect(() => {
    const onCounts = (c: SupportCounts) => setValue(c)
    const onNew = (a: SupportAlert) => alertRef.current?.(a)
    countListeners.add(onCounts)
    alertListeners.add(onNew)
    users += 1
    if (users === 1) void loadCounts()
    setValue(counts)
    connect()
    return () => {
      countListeners.delete(onCounts)
      alertListeners.delete(onNew)
      users -= 1
      if (!users) disconnect()
    }
  }, [])

  return value
}
